/* eslint-disable */

import '@reactivex/rxjs/dist/cjs/add/operator/debounceTime';
import { getURLParameters } from '../../client/startup/getURLParameters';
import { Observable, Subscription, Subject } from '@reactivex/rxjs';
import { fromPairs, max, values, each, map, flatten } from 'lodash';
const _ = { fromPairs, max, values, each, map, flatten };

import { wavefrontExpand } from '@graphistry/viz/src/utils/wavefrontExpand';
import {
    //aliased: this module still has its own legacy computeEdgeBundles for the
    //old expansion path; both go away together at final deletion
    computeEdgeBundles as computeEdgeInstanceBundles,
    computeEdgeBundleCurveFactors,
    buildEdgeInstanceAttrs,
    buildEdgeOverlayInstances,
    EDGE_STRIP_CORNERS
} from './edge-instances.ts';

//packed RGBA8 white: hover-highlight stroke color (edgehighlighted.vertex parity)
var EDGE_HIGHLIGHT_COLOR = 0xFFFFFFFF;

//instanced edgesdf items whose config-tracking uniforms (arrowEdgeSegments)
//the scheduler keeps in sync
var EDGE_SDF_ITEMS = [
    'edgesdf', 'edgesdfpicking', 'edgesdfhighlight', 'edgesdfselected',
    'edgesdfarrow', 'edgesdfarrowhighlight', 'edgesdfarrowselected'
];

import {
  backwardsEdgeToUnsortedEdge as backwardsEdgeToUnsortedEdgeSubject,
  backwardsEdgeStartEndIdxs as backwardsEdgeStartEndIdxsSubject,
} from "@graphistry/viz/src/client/legacy";

// const debug = (...args) => {};
// import { sprintf as printf } from 'sprintf-js';
// const debug = (...args) => console.log(`StreamGL:RenderingScheduler:`, printf(...args));

RenderingScheduler.prototype = Object.create(Subscription.prototype);

function RenderingScheduler(renderState, renderer, hitmapUpdates, isAnimating) {
    Subscription.call(this);

    this.renderer = renderer;
    this.renderState = renderState;
    this.arrayBuffers = {};
    //edge-count threshold above which fast frames force edges and nodes
    //opaque, engaging the effective-opacity-keyed depth features (see
    //prepareFrame). ?depthOcclusion=<n> overrides, =true (or 0) always
    //forces, =false never forces.
    var depthOcclusion = (typeof window !== 'undefined'
        ? getURLParameters(window.location.href) : {}).depthOcclusion;
    this.forceDepthOcclusionThreshold =
        depthOcclusion === false ? Infinity
        : depthOcclusion === true ? 0
        : typeof depthOcclusion === 'number' ? depthOcclusion
        : 5000000;
    // Remember last task in case you need to rerender mouseovers without an update.
    this.lastMouseoverTask = undefined;
    this.lastEdgesData = { curvature: 0.05, curvatureChanged: false };
    // Set by renderSlowEffects; the hitmap render is deferred until a pointer
    // needs it (renderPickingLazily, called from scene.js mouse handlers)
    this.pickingDirty = false;

    var config = renderState.config;

    /* Rendering queue */
    var renderTasks = new Subject();
    var renderQueue = Object.create(null);
    var renderingPaused = true; // False when the animation loop is running.

    var fullBufferNameList = config.buffers.concat(
        //TODO move client-only into render.config dummies when more sane
        [
            'backwardsEdgeStartEndIdxs',
            'backwardsEdgeToUnsortedEdge',
            //CPU-only inputs to the highlight/selected paths in edgesdf mode,
            //where no scene item references the midSprings models anymore
            //(duplicates with config.buffers are harmless in fromPairs)
            'midSpringsPos',
            'midSpringsStarts',
            'midSpringsEnds',
            'highlightedEdges',
            'highlightedNodeIds',
            'highlightedNodeSizes',
            'highlightedNodeColors',
            'highlightedArrowStartPos',
            'highlightedArrowEndPos',
            'highlightedArrowNormalDir',
            'highlightedArrowPointColors',
            'highlightedArrowPointSizes',
            'selectedEdges',
            'selectedNodeIds',
            'selectedNodeSizes',
            'selectedNodeColors',
            'selectedArrowStartPos',
            'selectedArrowEndPos',
            'selectedArrowNormalDir',
            'selectedArrowPointColors',
            'selectedArrowPointSizes',
            'selectedEdgeColors',
            'selectedEdgeEnds',
            'selectedEdgeStarts'
        ]
    );

    /* Since we cannot read out of Rx streams within the animation frame, we record the latest
     * value produced by needed rx streams and pass them as function arguments to the quiet state
     * callback. */
    this.appSnapshot = {
        hitmapUpdates,
        vboUpdated: false,
        simulating: false,
        quietState: false,
        interpolateMidPoints: true,
        fullScreenBufferDirty: true,

        //{ <activeBufferName> -> undefined}
        // Seem to be client-defined local buffers
        buffers: _.fromPairs(
            fullBufferNameList.map(function(v) {
                return [v, undefined];
            })
        ),

        bufferComputedVersions: _.fromPairs(
            fullBufferNameList.map(function(v) {
                return [v, -1];
            })
        ),

        bufferReceivedVersions: _.fromPairs(
            fullBufferNameList.map(function(v) {
                return [v, -1];
            })
        ),
    };

    Object.seal(this.appSnapshot);
    Object.seal(this.appSnapshot.buffers);

    /* Push a render task into the renderer queue
     * String * {trigger, items, readPixels, callback} -> () */
    this.renderScene = function(tag, task) {
        if (!task) {
            //likely init issue
            console.warn('renderScene with no task', new Error('renderScene: null task'));
            return;
        }
        renderTasks.onNext({
            tag: tag,
            trigger: task.trigger,
            items: task.items,
            readPixels: task.readPixels,
            callback: task.callback,
            data: task.data
        });
    };

    /*
     * Set up fullscreen buffer for mouseover effects.
     */
    renderer.setupFullscreenBuffer(renderState);

    var PAUSE_RENDERING_DELAY = 500;
    var SETTLE_DELAY = 250;

    var lastRenderTime = 0;
    var quietSignaled = false;
    var quietRenderQueue = Object.create(null);
    var settleRequested = false;

    const followOnRenderDarkEffects = () => {
        // If anything is selected, we need to do the copy to texture + darken
        // TODO: Investigate performance of this.
        let lastMouseoverTask = this.lastMouseoverTask;
        if (lastMouseoverTask && (
            lastMouseoverTask.data.darken || (
            lastMouseoverTask.data.selected.edgeIndices.length > 0))
        ) {
            const tmpSelectedEdgeIndices = lastMouseoverTask.data.selected.edgeIndices;
            lastMouseoverTask.data.selected.edgeIndices = [];
            renderer.copyCanvasToTexture(renderState, 'steadyStateTexture');
            renderer.setupFullscreenBuffer(renderState);
            this.renderMouseoverEffects(lastMouseoverTask);
            lastMouseoverTask.data.selected.edgeIndices = tmpSelectedEdgeIndices;
        }
    };

    // Communication between render loops about whether to update lastRenderTime,
    // or to check the delta against it to see if we should render slow effects.
    var shouldUpdateRenderTime = true;

    const loop = () => {
        let renderTaskNames = Object.keys(renderQueue);

        // Nothing to render
        if (renderTaskNames.length === 0) {
            // TODO: Generalize this
            if (!quietSignaled) {
                quietSignaled = true;
                isAnimating.onNext(false);
            }

            if (shouldUpdateRenderTime) {
                // Just update render time, leave delta checks for next loop
                lastRenderTime = Date.now();
                shouldUpdateRenderTime = false;
            } else {
                // Check time since last render. Based on duration, settle, then pause the rendering loop.
                var timeDelta = Date.now() - lastRenderTime;
                if (settleRequested && timeDelta > SETTLE_DELAY) {
                    settleRequested = false;
                    this.renderSlowEffects();
                    this.appSnapshot.vboUpdated = false;
                    shouldUpdateRenderTime = true;
                } else if (timeDelta > PAUSE_RENDERING_DELAY) {
                    let tag;
                    for (tag in quietRenderQueue) {
                        renderQueue[tag] = quietRenderQueue[tag];
                    }
                    // A fun way to check whether the `quietRenderQueue` enumerated any elements.
                    // - If not, pause the rendering loop.
                    // - If so, loop around again to render slow things.
                    if (tag === undefined) {
                        return pauseRenderingLoop();
                    }
                    quietRenderQueue = Object.create(null);
                }
            }
            return requestAnimationFrame(loop);
        }

        renderer.setCamera(renderState);
        this.prepareFrame(renderQueue);

        // Handle "slow effects request"
        // TODO: Handle this naturally, instead of hack here
        var handledSpecialTasks = false;
        var tagsWithRenderFull = renderTaskNames.filter(
            key => renderQueue[key].trigger === 'renderSceneFull'
        );

        if (tagsWithRenderFull.length > 0) {

            // TODO: Generalize this code block
            handledSpecialTasks = true;
            shouldUpdateRenderTime = true;
            this.appSnapshot.fullScreenBufferDirty = true;
            if (quietSignaled) {
                //visible frame now, settle once rendering goes quiet for SETTLE_DELAY
                renderer.render(renderState, 'fullscene', 'renderSceneFull');
                settleRequested = true;
            }
            for (let i = -1, n = tagsWithRenderFull.length; ++i < n; ) {
                const tag = tagsWithRenderFull[i];
                !quietSignaled && (quietRenderQueue[tag] = renderQueue[tag]);
                delete renderQueue[tag];
            }
        }

        // Move points overlay
        // Takes precedence over mouseover interactions, and will skip mouseover
        // interactions
        if ('movePointsOverlay' in renderQueue) {
            if (!this.appSnapshot.fullScreenBufferDirty) {
                shouldUpdateRenderTime = true;
                this.renderMovePointsOverlay(renderQueue.movePointsOverlay);
            }
            handledSpecialTasks = true;
            delete renderQueue.mouseOver;
            delete renderQueue.movePointsOverlay;
        } else if ('mouseOver' in renderQueue) {
            // Mouseover
            // TODO: Generalize this as a separate category?
            // Only handle mouseovers if the fullscreen buffer
            // from rendering all edges (full scene) is clean
            if (!this.appSnapshot.fullScreenBufferDirty) {
                shouldUpdateRenderTime = true;
                this.renderMouseoverEffects(renderQueue.mouseOver);
            }
            handledSpecialTasks = true;
            delete renderQueue.mouseOver;
        }

        handledSpecialTasks && (renderTaskNames = Object.keys(renderQueue));

        // Rest of render queue
        if (renderTaskNames.length > 0) {

            let isRenderingToScreen = false;

            // TODO: Generalize this into tag description (or allow to check renderconfig)
            // Alternatively, generalize when we fix the fullScreenBuffer.
            for (let i = -1, n = renderTaskNames.length; ++i < n; ) {
                const taskName = renderTaskNames[i];
                // TODO: Generalize this code block
                if (taskName.indexOf('picking') === -1) {
                    isRenderingToScreen = true;
                    shouldUpdateRenderTime = true;
                    this.appSnapshot.fullScreenBufferDirty = true;
                    if (quietSignaled) {
                        quietSignaled = false;
                        isAnimating.onNext(true);
                    }
                    break;
                }
            }

            const renderedTriggers = {};
            for (let i = -1, n = renderTaskNames.length; ++i < n; ) {
                const taskName = renderTaskNames[i];
                const renderTask = renderQueue[taskName];
                delete renderQueue[taskName];
                //the queue dedupes per TAG, so same-frame tasks sharing a
                //trigger redrew the whole scene back-to-back (doubled tick
                //frames at 9.18M). Render each trigger once; tasks with
                //payloads (picking, mouseover) still run individually.
                if (!renderTask.items && !renderTask.readPixels && !renderTask.callback) {
                    if (renderedTriggers[renderTask.trigger]) {
                        continue;
                    }
                    renderedTriggers[renderTask.trigger] = true;
                }
                renderer.render(
                    renderState,
                    taskName,
                    renderTask.trigger,
                    renderTask.items,
                    renderTask.readPixels,
                    renderTask.callback
                );
            }

            // If anything is selected, we need to do the copy to texture + darken
            isRenderingToScreen && followOnRenderDarkEffects();
        }
        requestAnimationFrame(loop);
    };

    /*
     * Helpers to start/stop the rendering loop within an animation frame. The rendering loop
     * stops when idle for a second and starts again at the next render update.
     */
    function startRenderingLoop() {
        // debug('Starting rendering loop');
        renderingPaused = false;
        requestAnimationFrame(loop);
    }

    function pauseRenderingLoop() {
        // debug('Pausing rendering loop');
        renderingPaused = true;
    }

    /* Move render tasks into a tagged dictionary. For each tag, only the latest task
     * is rendered; others are skipped. */
    this.add(
        renderTasks.subscribe(function(task) {
            renderQueue[task.tag] = task;
            if (renderingPaused) {
                // debug('Queueing frame on behalf of %s', task.tag);
                startRenderingLoop();
            }
        })
    );
}

// Hook to preallocate memory when initial sizes are available.
// We handle these by putting them into an subject and handling
// each with a 1ms delay in between, to give the JS thread
// some breathing room to handle other callbacks/repaints.
RenderingScheduler.prototype.attemptToAllocateBuffersOnHints = function(
    renderState,
    { edges = 0, points = 0 } = {}
) {
    const { numHintElements = {} } = this;

    if (numHintElements.edges === edges && numHintElements.points === points) {
        return;
    }

    if (edges === 0 && points === 0) {
        return;
    }

    const { hintsAllocationCycle } = this;

    if (hintsAllocationCycle) {
        this.remove(hintsAllocationCycle);
        hintsAllocationCycle.unsubscribe();
        return;
    }

    const renderer = this.renderer;
    const config = renderState.config;

    const timeoutLength = 1;
    const MAX_SIZE_TO_ALLOCATE = 2000000;

    edges = Math.min(edges, MAX_SIZE_TO_ALLOCATE);
    points = Math.min(points, MAX_SIZE_TO_ALLOCATE);

    const numElements = {
        edges, edge: edges,
        points, point: points,
        renderedSplits: config.numRenderedSplits
    };

    this.numHintElements = numElements;

    const activeIndices = renderState.activeIndices;
    const largestModel = this.getLargestModelSize(config, numElements);
    const maxElements = Math.max(_.max(_.values(numElements)), largestModel);
    const allocationFunctions = this.allocateAllArrayBuffersFactory(config, numElements, renderState);
    const hintsAllocationObs = Observable.from(allocationFunctions.concat(activeIndices.map((index) =>
        renderer.updateIndexBuffer.bind('', renderState, maxElements, index)
    )))
    .concatMap((allocationFunction) => {
        // Do one big job, then increment
        allocationFunction();
        // Cede control to browser, then handle next element
        return Observable.timer(timeoutLength).take(1);
    }).publish().refCount();

    this.hintsAllocationObs = hintsAllocationObs;
    this.hintsAllocationCycle = hintsAllocationObs.subscribe(null, null, () => {
        // debug('Finished allocating buffers for hints', numElements);
    })
    this.add(this.hintsAllocationCycle);
    return hintsAllocationObs;
};

//int * int * Int32Array * Float32Array -> {starts: Float32Array, ends: Float32Array}
//Scatter: label each midEdge with containing edge's start/end pos (used for dynamic culling)
RenderingScheduler.prototype.expandMidEdgeEndpoints = function(
    numEdges,
    numRenderedSplits,
    logicalEdges,
    curPoints
) {
    // var starts = new Float32Array(numEdges * (numRenderedSplits + 1) * 4);
    // var ends = new Float32Array(numEdges * (numRenderedSplits + 1) * 4);

    var starts = this.getTypedArray('midSpringsStarts', Float32Array, numEdges * (numRenderedSplits + 1) * 4);
    var ends = this.getTypedArray('midSpringsEnds', Float32Array, numEdges * (numRenderedSplits + 1) * 4);

    var offset = 0;

    for (var edgeIndex = 0; edgeIndex < numEdges; edgeIndex++) {
        var srcPointIdx = logicalEdges[edgeIndex * 2] * 2;
        var dstPointIdx = logicalEdges[edgeIndex * 2 + 1] * 2;
        var srcPointX = curPoints[srcPointIdx];
        var srcPointY = curPoints[srcPointIdx + 1];
        var dstPointX = curPoints[dstPointIdx];
        var dstPointY = curPoints[dstPointIdx + 1];

        for (var midPointIdx = 0; midPointIdx < numRenderedSplits + 1; midPointIdx++) {
            starts[offset] = srcPointX;
            starts[offset + 1] = srcPointY;
            starts[offset + 2] = srcPointX;
            starts[offset + 3] = srcPointY;
            ends[offset] = dstPointX;
            ends[offset + 1] = dstPointY;
            ends[offset + 2] = dstPointX;
            ends[offset + 3] = dstPointY;
            offset += 4;
        }
    }

    return { starts: starts, ends: ends };
};

//[ srcIdx, dstIdx, ... ] -> { bundleLens: Uint32Array, bundleEntry: Uint32Array }
// Identify multiedges: For each edge, # multiedges in its group, and its index in that list
// ~50ms-100ms on a big graph
function computeEdgeBundles(logicalEdges) {
    const numEdges = logicalEdges.length / 2;

    const numInBundle = new Uint32Array(numEdges);
    const bundleEntry = new Uint32Array(numEdges);

    //TODO this is data that could have been sent from the server, and stable between filters
    // or locally avoidable if the array was sorted
    const src2dstCounts = {};
    for (let edgeIndex = 0; edgeIndex < numEdges; edgeIndex++) {
        const srcPointIdx = logicalEdges[2 * edgeIndex];
        const dstPointIdx = logicalEdges[2 * edgeIndex + 1];
        const dstCounts = (src2dstCounts[srcPointIdx] = src2dstCounts[srcPointIdx] || {});
        dstCounts[dstPointIdx] = (dstCounts[dstPointIdx] || 0) + 1;
    }

    const src2dstCountsRolling = {};
    for (let edgeIndex = 0; edgeIndex < numEdges; edgeIndex++) {
        const srcPointIdx = logicalEdges[2 * edgeIndex];
        const dstPointIdx = logicalEdges[2 * edgeIndex + 1];
        const dstCounts = (src2dstCountsRolling[srcPointIdx] =
            src2dstCountsRolling[srcPointIdx] || {});
        const bundleIndex = (dstCounts[dstPointIdx] = dstCounts[dstPointIdx] + 1 || 0);

        const bundleLen = src2dstCounts[srcPointIdx][dstPointIdx];

        numInBundle[edgeIndex] = bundleLen;
        bundleEntry[edgeIndex] = bundleIndex;
    }

    return { numInBundle, bundleEntry };
}

//For edge bundles of same size, memoize height geometry per edge
//  -- also fill in cosArray, sinArray (for examplars)
//TODO memoize across calls by preserving cosArray/sinArray
function updateEdgeCache({
    numEdges,
    numInBundle,
    bundleEntry,
    edgeHeight,
    numRenderedSplits,
    cosArray,
    sinArray
}) {
    const edgeCache = {};

    for (let edgeIndex = 0; edgeIndex < numEdges; edgeIndex++) {
        const bundleLen = numInBundle[edgeIndex];
        const edgeNum = bundleEntry[edgeIndex];

        if (!edgeCache[bundleLen]) {
            edgeCache[bundleLen] = [];
        }
        if (!edgeCache[bundleLen][edgeNum]) {
            const curveArrayOffset = edgeIndex * numRenderedSplits;
            const moduloHeight = edgeHeight * (1.0 + 2 * edgeNum / bundleLen);
            const unitRadius = (1 + Math.pow(moduloHeight, 2)) / (2 * moduloHeight);
            const theta = Math.asin(1 / unitRadius) * 2;
            const thetaStep = -theta / (numRenderedSplits + 1);
            for (let midPointIdx = 0; midPointIdx < numRenderedSplits; midPointIdx++) {
                const curTheta = thetaStep * (midPointIdx + 1);
                cosArray[curveArrayOffset + midPointIdx] = Math.cos(curTheta);
                sinArray[curveArrayOffset + midPointIdx] = Math.sin(curTheta);
            }
            edgeCache[bundleLen][edgeNum] = { moduloHeight, unitRadius, curveArrayOffset };
        }
    }

    return edgeCache;
}
// RenderState
// {logicalEdges: Uint32Array, curPoints: Float32Array, edgeHeights: Float32Array, ?midSpringsPos: Float32Array}
//  * int * float
//  -> {midSpringsPos: Float32Array, midSpringsStarts: Float32Array, midSpringsEnds: Float32Array}
RenderingScheduler.prototype.expandLogicalEdgesStraight = function(bufferSnapshots, numRenderedSplits) {

    const logicalEdges = castToTypedArray(bufferSnapshots.logicalEdges, Uint32Array);
    const curPoints = castToTypedArray(bufferSnapshots.curPoints, Float32Array);

    const numEdges = logicalEdges.length / 2;

    const numVertices = 2 * numEdges * (numRenderedSplits + 1);

    bufferSnapshots.midSpringsPos = this.getTypedArray(
        'midSpringsPos',
        Float32Array,
        numVertices * 2
    );

    const midSpringsPos = bufferSnapshots.midSpringsPos;
    const midEdgesPerEdge = numRenderedSplits + 1;
    const midEdgeStride = 4 * midEdgesPerEdge;

    //for each midEdge, start x/y & end x/y
    const midSpringsEndpoints = this.expandMidEdgeEndpoints(
        numEdges,
        numRenderedSplits,
        logicalEdges,
        curPoints
    );
    
    for (let edgeIndex = 0; edgeIndex < numEdges; edgeIndex++) {
                
        const srcPointIdx = logicalEdges[2 * edgeIndex];
        const dstPointIdx = logicalEdges[2 * edgeIndex + 1];
        const srcPointX = curPoints[2 * srcPointIdx];
        const srcPointY = curPoints[2 * srcPointIdx + 1];
        const dstPointX = curPoints[2 * dstPointIdx];
        const dstPointY = curPoints[2 * dstPointIdx + 1];        

        /////////////

        let prevPointX = srcPointX;
        let prevPointY = srcPointY;
        let stepX = (dstPointX - srcPointX) / numRenderedSplits;
        let stepY = (dstPointY - srcPointY) / numRenderedSplits;

        for (let midPointIdx = 0; midPointIdx < numRenderedSplits; midPointIdx++) {
            const nextPointX = prevPointX + stepX;
            const nextPointY = prevPointY + stepY;
            const midEdgeStartIdx = edgeIndex * midEdgeStride;
            const index = midEdgeStartIdx + midPointIdx * 4;
            midSpringsPos[index] = prevPointX;
            midSpringsPos[index + 1] = prevPointY;
            midSpringsPos[index + 2] = nextPointX;
            midSpringsPos[index + 3] = nextPointY;
            prevPointX = nextPointX;
            prevPointY = nextPointY;
        }
        const midEdgeStartIdx = edgeIndex * midEdgeStride;
        const index = midEdgeStartIdx + numRenderedSplits * 4;
        midSpringsPos[index] = prevPointX;
        midSpringsPos[index + 1] = prevPointY;
        midSpringsPos[index + 2] = dstPointX;
        midSpringsPos[index + 3] = dstPointY;
    }

    return {
        midSpringsPos: midSpringsPos,
        midSpringsStarts: midSpringsEndpoints.starts,
        midSpringsEnds: midSpringsEndpoints.ends
    };
}

// RenderState
// {logicalEdges: Uint32Array, curPoints: Float32Array, edgeHeights: Float32Array, ?midSpringsPos: Float32Array}
//  * int * float
//  -> {midSpringsPos: Float32Array, midSpringsStarts: Float32Array, midSpringsEnds: Float32Array}
RenderingScheduler.prototype.expandLogicalEdges = function(bufferSnapshots, numRenderedSplits, edgeHeight) {

    if (edgeHeight < 0.01) {
        return this.expandLogicalEdgesStraight(bufferSnapshots, numRenderedSplits);
    }

    const logicalEdges = castToTypedArray(bufferSnapshots.logicalEdges, Uint32Array);
    const curPoints = castToTypedArray(bufferSnapshots.curPoints, Float32Array);

    const numEdges = logicalEdges.length / 2;

    const numVertices = 2 * numEdges * (numRenderedSplits + 1);

    bufferSnapshots.midSpringsPos = this.getTypedArray('midSpringsPos', Float32Array, numVertices * 2);

    const midSpringsPos = bufferSnapshots.midSpringsPos;
    const midEdgesPerEdge = numRenderedSplits + 1;
    const midEdgeStride = 4 * midEdgesPerEdge;

    //for each midEdge, start x/y & end x/y
    const midSpringsEndpoints = this.expandMidEdgeEndpoints(numEdges, numRenderedSplits, logicalEdges, curPoints);

    const { numInBundle, bundleEntry } = computeEdgeBundles(logicalEdges);

    //~50-100ms on a big graph
    const cosArray = this.getTypedArray('edgeBundlesCosArray', Float32Array, numRenderedSplits * numEdges);// new Float32Array(numRenderedSplits * numEdges);
    const sinArray = this.getTypedArray('edgeBundlesSinArray', Float32Array, numRenderedSplits * numEdges);// new Float32Array(numRenderedSplits * numEdges);
    const edgeCache = updateEdgeCache({ numEdges, numInBundle, bundleEntry, edgeHeight, numRenderedSplits, cosArray, sinArray });

    //~50-100ms on a big graph
    for (let edgeIndex = 0; edgeIndex < numEdges; edgeIndex++) {
        /////////////

        const srcPointIdx = logicalEdges[2 * edgeIndex];
        const dstPointIdx = logicalEdges[2 * edgeIndex + 1];
        const srcPointX = curPoints[2 * srcPointIdx];
        const srcPointY = curPoints[2 * srcPointIdx + 1];
        const dstPointX = curPoints[2 * dstPointIdx];
        const dstPointY = curPoints[2 * dstPointIdx + 1];

        const edgeNum = bundleEntry[edgeIndex];
        const bundleLen = numInBundle[edgeIndex];

        /////////////

        const { moduloHeight, unitRadius, curveArrayOffset } = edgeCache[bundleLen][edgeNum];

        /////////////

        const edgeLength = srcPointIdx === dstPointIdx
            ? 1.0
            : Math.sqrt(Math.pow(dstPointX - srcPointX, 2) + Math.pow(dstPointY - srcPointY, 2));

        const height = moduloHeight * (edgeLength / 2);
        const edgeDirectionX = (srcPointX - dstPointX) / edgeLength;
        const edgeDirectionY = (srcPointY - dstPointY) / edgeLength;
        const radius = unitRadius * (edgeLength / 2);
        const midPointX = (srcPointX + dstPointX) / 2;
        const midPointY = (srcPointY + dstPointY) / 2;
        const centerPointX = midPointX + (radius - height) * (-1 * edgeDirectionY);
        const centerPointY = midPointY + (radius - height) * edgeDirectionX;
        const startRadiusX = srcPointIdx === dstPointIdx ? 1.0 : srcPointX - centerPointX;
        const startRadiusY = srcPointIdx === dstPointIdx ? 1.0 : srcPointY - centerPointY;

        let prevPointX = srcPointX;
        let prevPointY = srcPointY;

        for (let midPointIdx = 0; midPointIdx < numRenderedSplits; midPointIdx++) {
            const cos = cosArray[curveArrayOffset + midPointIdx];
            const sin = sinArray[curveArrayOffset + midPointIdx];
            const nextPointX = centerPointX + cos * startRadiusX - sin * startRadiusY;
            const nextPointY = centerPointY + sin * startRadiusX + cos * startRadiusY;

            const midEdgeStartIdx = edgeIndex * midEdgeStride;
            const index = midEdgeStartIdx + midPointIdx * 4;
            midSpringsPos[index] = prevPointX;
            midSpringsPos[index + 1] = prevPointY;
            midSpringsPos[index + 2] = nextPointX;
            midSpringsPos[index + 3] = nextPointY;
            prevPointX = nextPointX;
            prevPointY = nextPointY;
        }
        const midEdgeStartIdx = edgeIndex * midEdgeStride;
        const index = midEdgeStartIdx + numRenderedSplits * 4;
        midSpringsPos[index] = prevPointX;
        midSpringsPos[index + 1] = prevPointY;
        midSpringsPos[index + 2] = dstPointX;
        midSpringsPos[index + 3] = dstPointY;
    }

    return {
        midSpringsPos: midSpringsPos,
        midSpringsStarts: midSpringsEndpoints.starts,
        midSpringsEnds: midSpringsEndpoints.ends
    };
};

/* Populate arrow buffers. The first argument is either an array of indices,
 * or an integer value of how many you want.
 * When compactGeometry is set, midSpringsPos holds segments packed by position
 * in the iterable (edge-instances.ts subset layout) instead of by edge id.
 */
RenderingScheduler.prototype.populateArrowBuffers = function(
    maybeIterable,
    midSpringsPos,
    arrowStartPos,
    arrowEndPos,
    arrowNormalDir,
    pointSizes,
    logicalEdges,
    arrowPointSizes,
    arrowColors,
    edgeColors,
    numRenderedSplits,
    compactGeometry
) {
    var edgeColors32 = castToTypedArray(edgeColors, Uint32Array);

    var numMidEdges = numRenderedSplits + 1;

    var isIterable = maybeIterable.constructor === Array;
    var forLimit = isIterable ? maybeIterable.length : maybeIterable;

    //var start = new Float32Array(2);
    //var end = new Float32Array(2);
    var startX, startY, endX, endY;
    for (var idx = 0; idx < forLimit; idx++) {
        var val = isIterable ? maybeIterable[idx] : idx;

        var midEdgeIdx = ((compactGeometry ? idx : val) + 1) * (numMidEdges * 4) - 4;
        startX = midSpringsPos[midEdgeIdx + 0];
        startY = midSpringsPos[midEdgeIdx + 1];
        endX = midSpringsPos[midEdgeIdx + 2];
        endY = midSpringsPos[midEdgeIdx + 3];

        arrowStartPos[6 * idx + 0] = startX;
        arrowStartPos[6 * idx + 1] = startY;
        arrowStartPos[6 * idx + 2] = startX;
        arrowStartPos[6 * idx + 3] = startY;
        arrowStartPos[6 * idx + 4] = startX;
        arrowStartPos[6 * idx + 5] = startY;

        arrowEndPos[6 * idx + 0] = endX;
        arrowEndPos[6 * idx + 1] = endY;
        arrowEndPos[6 * idx + 2] = endX;
        arrowEndPos[6 * idx + 3] = endY;
        arrowEndPos[6 * idx + 4] = endX;
        arrowEndPos[6 * idx + 5] = endY;

        arrowNormalDir[3 * idx + 0] = 0; // Tip vertex
        arrowNormalDir[3 * idx + 1] = 1; // Left vertex
        arrowNormalDir[3 * idx + 2] = -1; // Right vertex

        var pointSize = pointSizes[logicalEdges[2 * val + 1]];
        arrowPointSizes[3 * idx + 0] = pointSize;
        arrowPointSizes[3 * idx + 1] = pointSize;
        arrowPointSizes[3 * idx + 2] = pointSize;

        arrowColors[3 * idx + 0] = edgeColors32[2 * val + 1];
        arrowColors[3 * idx + 1] = edgeColors32[2 * val + 1];
        arrowColors[3 * idx + 2] = edgeColors32[2 * val + 1];
    }
};

RenderingScheduler.prototype.getMidEdgeColors = function(bufferSnapshot, numEdges, numRenderedSplits) {

    const numSegments = numRenderedSplits + 1;
    const edgeColors = castToTypedArray(bufferSnapshot.edgeColors, Uint32Array);
    const midEdgeColors = this.getTypedArray('midEdgesColors', Uint32Array, numEdges * numSegments);

    const segmentColorsCache = Object.create(null);
    const getFromCache = (src, dst) => segmentColorsCache[src] && segmentColorsCache[src][dst] || null;
    const putInCache = (src, dst, val) => (segmentColorsCache[src] || (segmentColorsCache[src] = Object.create(null)))[dst] = val;

    for (let colors, edge = -1, n = numEdges / 2; ++edge < n;) {

        const srcColorInt = edgeColors[edge * 2];
        const dstColorInt = edgeColors[edge * 2 + 1];
        const midEdgeColorIndex = 2 * edge * numSegments;

        if (!(colors = getFromCache(srcColorInt, dstColorInt))) {

            colors = new Uint32Array(numSegments * 2);

            // Convert from RGB Int to HSV
            const srcR = (srcColorInt) & 0xff;
            const srcG = (srcColorInt >> 8) & 0xff;
            const srcB = (srcColorInt >> 16) & 0xff;
    
            const dstR = (dstColorInt) & 0xff;
            const dstG = (dstColorInt >> 8) & 0xff;
            const dstB = (dstColorInt >> 16) & 0xff;

            let interpolatedColor = colors[0] = srcColorInt;

            for (let midEdge = -1; ++midEdge < numSegments;) {

                colors[midEdge * 2] = interpolatedColor;

                const lambda = (midEdge + 1) / numSegments;
                const r = srcR * (1 - lambda) + dstR * lambda;
                const g = srcG * (1 - lambda) + dstG * lambda;
                const b = srcB * (1 - lambda) + dstB * lambda;

                // Convert from HSV to RGB Int
                interpolatedColor = (r << 0) + (g << 8) + (b << 16);

                colors[midEdge * 2 + 1] = interpolatedColor;
            }
            putInCache(srcColorInt, dstColorInt, colors);
        }

        midEdgeColors.set(colors, midEdgeColorIndex);
    }

    return midEdgeColors;
};

RenderingScheduler.prototype.makeArrows = function(bufferSnapshots, edgeMode, numRenderedSplits) {
    if (!bufferSnapshots || !bufferSnapshots.logicalEdges || !bufferSnapshots.pointSizes || !bufferSnapshots.edgeColors) {
        const err = new Error('makeArrows missing bufferSnapshots');
        console.error('makeArrows', { err,  bufferSnapshots });
        throw err;
    }
    var logicalEdges = castToTypedArray(bufferSnapshots.logicalEdges, Uint32Array);
    var pointSizes = castToTypedArray(bufferSnapshots.pointSizes, Uint8Array);
    var edgeColors = castToTypedArray(bufferSnapshots.edgeColors, Uint32Array);
    var numEdges = logicalEdges.length / 2;
    var arrowStartPos = bufferSnapshots.arrowStartPos = this.getTypedArray('arrowStartPos', Float32Array, numEdges * 2 * 3);
    var arrowEndPos = bufferSnapshots.arrowEndPos = this.getTypedArray('arrowEndPos', Float32Array, numEdges * 2 * 3);
    var arrowNormalDir = bufferSnapshots.arrowNormalDir = this.getTypedArray('arrowNormalDir', Float32Array, numEdges * 3);
    var arrowColors = bufferSnapshots.arrowColors = this.getTypedArray('arrowColors', Uint32Array, numEdges * 3);
    var arrowPointSizes = bufferSnapshots.arrowPointSizes = this.getTypedArray('arrowPointSizes', Uint8Array, numEdges * 3);

    this.populateArrowBuffers(
        numEdges,
        bufferSnapshots.midSpringsPos,
        arrowStartPos,
        arrowEndPos,
        arrowNormalDir,
        pointSizes,
        logicalEdges,
        arrowPointSizes,
        arrowColors,
        edgeColors,
        numRenderedSplits
    );
};


RenderingScheduler.prototype.reverseIndexIncomingEdges = function (bufferSnapshots) {
    const numPoints = bufferSnapshots.pointSizes.length;
    const numEdges = bufferSnapshots.forwardsEdgeToUnsortedEdge.length;

    //node -> indegree
    const indegrees = this.getTypedArray('indegrees', Uint32Array, numPoints);
    indegrees.fill(0);
    for (let dstIdx = 1; dstIdx < numEdges * 2; dstIdx += 2) {
        const dst = bufferSnapshots.logicalEdges[dstIdx];
        indegrees[dst]++;
    }
    //2 * point -> first incoming edge idx in backwardsEdgeToUnsortedEdge
    //2 * point + 1 -> last incoming edge idx in backwardsEdgeToUnsortedEdge
    const backwardsEdgeStartEndIdxs = this.getTypedArray('backwardsEdgeStartEndIdxs', Uint32Array, numPoints * 2);
    let rolling = 0;
    let backwardsEdgeIdx = -1;
    for (let dst = 0; dst < numPoints; dst++) {
        backwardsEdgeStartEndIdxs[++backwardsEdgeIdx] = rolling;
        rolling += indegrees[dst];
        backwardsEdgeStartEndIdxs[++backwardsEdgeIdx] = rolling;
    }
    //point -> traversed edges for dst node 
    const seenBackwardsEdges = this.getTypedArray('seenBackwardsEdges', Uint32Array, numPoints);
    seenBackwardsEdges.fill(0);
    //backwardsEdgeStartEndIdxs position -> logical edge
    const backwardsEdgeToUnsortedEdge = this.getTypedArray('backwardsEdgeToUnsortedEdge', Uint32Array, numEdges);

    let edge = 0;
    for (let dstIdx = 1; dstIdx < numEdges * 2; dstIdx += 2) {
        const src = bufferSnapshots.logicalEdges[dstIdx - 1];
        const dst = bufferSnapshots.logicalEdges[dstIdx];
        const progress = seenBackwardsEdges[dst];
        seenBackwardsEdges[dst]++;
        const backwardsEdgeIdx = backwardsEdgeStartEndIdxs[2 * dst] + progress;
        backwardsEdgeToUnsortedEdge[backwardsEdgeIdx] = edge;
        edge++;
    }

    return { backwardsEdgeStartEndIdxs, backwardsEdgeToUnsortedEdge };
}



/*
 * Bundle multiplicity depends only on topology, so cache it across the
 * curvature/encoding changes that rebuild height classes. Invalidation uses
 * the per-buffer received-version counter (bumped when a logicalEdges VBO
 * arrives, but not on position-only updates) -- array identity is not a safe
 * key because partial VBO loads memcpy into the existing host buffer.
 */
RenderingScheduler.prototype.getEdgeBundles = function(logicalEdges, sortedPermutation) {
    var version = this.appSnapshot.bufferReceivedVersions.logicalEdges;
    var numEdges = logicalEdges.length / 2;
    var cache = this.edgeBundleCache;

    if (!cache || cache.version !== version || cache.numEdges !== numEdges) {
        //sortedPermutation is forwardsEdgeToUnsortedEdge, the FEP's GPU
        //(src,dst,id) sort -- the (src,dst)-grouped order the run-scan needs.
        cache = this.edgeBundleCache = {
            version: version,
            numEdges: numEdges,
            bundles: computeEdgeInstanceBundles(logicalEdges, sortedPermutation)
        };
    }

    return cache.bundles;
};

/*
 * Feed the instanced analytic-SDF edge item (edgesdf): static endpoint node
 * ids, colors, height classes, and arc LUT. Live positions come from the
 * per-node float texture updated independently at layout cadence.
 */
RenderingScheduler.prototype.updateEdgeInstances = function(bufferSnapshots, edgeHeight) {
    var renderState = this.renderState;
    var renderer = this.renderer;

    var logicalEdges = castToTypedArray(bufferSnapshots.logicalEdges, Uint32Array);
    var numEdges = logicalEdges.length / 2;

    if (!this.edgeStripCornersLoaded) {
        renderer.loadBuffers(renderState, {
            edgeStripCorners: EDGE_STRIP_CORNERS,
            arrowCornerDirs: Float32Array.of(0, 1, -1)
        });
        this.edgeStripCornersLoaded = true;
    }

    var topologyVersion = this.appSnapshot.bufferReceivedVersions.logicalEdges;
    var nodeIdsStale = this.lastEdgeInstanceNodeIdsTopology !== topologyVersion ||
        this.lastEdgeInstanceNodeIdsCount !== numEdges;
    if (nodeIdsStale) {
        renderer.loadBuffers(renderState, { edgeEndpointIds: logicalEdges });
        this.lastEdgeInstanceNodeIdsTopology = topologyVersion;
        this.lastEdgeInstanceNodeIdsCount = numEdges;
    }

    //Each shape factor is a function ONLY of bundle membership -- how many
    //parallel edges share the edge's directed (src,dst) pair and its rank among
    //them (getEdgeBundles -> computeEdgeBundleCurveFactors). It does NOT depend on
    //positions, colors, node sizes, or curvature: the global slider is applied
    //per-fragment via the edgeCurvature uniform (h = edgeCurvature * shapeFactor),
    //so a curvature change rebuilds NOTHING here -- only the uniform below.
    //Rebuild the factors on topology or count change; bundles are cached in
    //getEdgeBundles so this is a topology-cadence cost.
    var shapeFactors = this.edgeInstanceShapeFactors;
    var shapeFactorsStale = !shapeFactors ||
        shapeFactors.length !== numEdges ||
        this.lastEdgeInstanceTopology !== topologyVersion;

    if (shapeFactorsStale) {
        //Group parallel edges using forwardsEdgeToUnsortedEdge, which is
        //co-delivered with logicalEdges on every topology update.
        //sorted position -> edge index, sorted by (src,dst,id) on the GPU
        var sortedPermutation = castToTypedArray(bufferSnapshots.forwardsEdgeToUnsortedEdge, Uint32Array);
        var bundles = this.getEdgeBundles(logicalEdges, sortedPermutation);
        shapeFactors = this.edgeInstanceShapeFactors =
            computeEdgeBundleCurveFactors(logicalEdges, bundles);
        this.lastEdgeInstanceTopology = topologyVersion;
    }

    //The attr record interleaves colors + shapeFactor + dstPointSize, so a
    //change to ANY of them rewrites the whole record (buildEdgeInstanceAttrs
    //+ upload, ~15ms at 1.6M).
    var colorsChanged = this.shouldUpdateEdgeColors(!bufferSnapshots.edgeStyleAttrs);
    var pointSizesVersion = this.appSnapshot.bufferReceivedVersions.pointSizes;
    var attrsStale = shapeFactorsStale || colorsChanged ||
        !bufferSnapshots.edgeStyleAttrs ||
        bufferSnapshots.edgeStyleAttrs.length !== numEdges * 4 ||
        this.lastEdgeInstancePointSizes !== pointSizesVersion;

    if (attrsStale) {
        var edgeColors = castToTypedArray(bufferSnapshots.edgeColors, Uint32Array);
        var pointSizes = bufferSnapshots.pointSizes &&
            castToTypedArray(bufferSnapshots.pointSizes, Uint8Array);
        var attrs = buildEdgeInstanceAttrs(
            edgeColors,
            shapeFactors,
            this.getTypedArray('edgeStyleAttrs', Uint32Array, numEdges * 4),
            logicalEdges,
            pointSizes
        );
        bufferSnapshots.edgeStyleAttrs = attrs;
        renderer.loadBuffers(renderState, { edgeStyleAttrs: attrs });
        this.lastEdgeInstancePointSizes = pointSizesVersion;
    }

    renderer.setNumElements(renderState, 'edgesdf', numEdges);
    if (renderState.config.items.edgesdfpicking) {
        //x(splits+1) shim so picking.js's legacy /(splits+1) decode recovers the correct edge id
        renderState.uniforms.edgesdfpicking.pickMultiplier[0] = renderState.config.numRenderedSplits + 1;
        renderer.setNumElements(renderState, 'edgesdfpicking', numEdges);
    }
    //set shared uniforms on all edgesdf items -- in place: this also runs per
    //fast frame via updateEdgeInstancesForFastRender, so no per-frame arrays
    var edgeCurvatureUniform = edgeHeight < 0.01 ? 0.0 : edgeHeight;
    //hairline floor: 2 display px in buffer px (the canvas is pixelRatio-x
    //oversampled; see edgesdf.vertex.glsl). Seeds the settle default and the
    //overlays -- prepareFrame re-floors the main stroke item every frame.
    var minStrokeWidthPx = 2 * renderState.camera.pixelRatio;
    var arrowSegments = renderState.config.numRenderedSplits + 1;
    EDGE_SDF_ITEMS.forEach(function(itemName) {
        var uniformsMap = renderState.uniforms[itemName];
        uniformsMap.edgeCurvature[0] = edgeCurvatureUniform;
        //edgesdfpicking has no arrowhead cutoff, so no arrowEdgeSegments uniform
        if (uniformsMap.arrowEdgeSegments) {
            uniformsMap.arrowEdgeSegments[0] = arrowSegments;
        }
        //stroke items only (picking keeps its own unfloored width)
        if (uniformsMap.minStrokeWidthPx) {
            uniformsMap.minStrokeWidthPx[0] = minStrokeWidthPx;
        }
    });
    renderer.setNumElements(renderState, 'edgesdfarrow', numEdges);
};

/*
 * Frame-cadence data maintenance, called from the render loop once per
 * processed frame BEFORE any task draws (settle special-case included): keeps
 * the declared data textures current, plus the SDF instance buffers and
 * fast-frame uniform state when a fast (layout/motion) draw is pending.
 * Everything inside is version-gated; a clean frame costs a few comparisons.
 * Must stay in the loop: upstream enqueues at prop-stream rate (many times
 * per painted frame), and the once-per-frame drain is what coalesces the
 * texture upload to paint cadence.
 */
RenderingScheduler.prototype.prepareFrame = function(renderQueue) {
    //self-gating: scenes without source-declared data textures no-op
    this.syncDataTextures();
    if (!this.renderState.config.items.edgesdf) {
        return;
    }
    var fastPending = false;
    for (var taskName in renderQueue) {
        if (renderQueue[taskName].trigger === 'renderSceneFast') {
            fastPending = true;
            this.updateEdgeInstancesForFastRender();
            break;
        }
    }
    //the items and uniforms below exist statically in every edgesdf scene
    //(items.js); mutate uniform arrays in place -- this 60fps path must stay
    //allocation-free.
    var uniforms = this.renderState.uniforms;
    //motion frames drop the hairline floor to 1 display px: phase shimmer is
    //invisible while everything moves (widthFade keeps perceived density) and
    //the narrower hull cuts the stroke draw. Settle restores the 2px floor.
    uniforms.edgesdf.minStrokeWidthPx[0] =
        (fastPending ? 1 : 2) * this.renderState.camera.pixelRatio;
    //the edge-count gate controls one thing: force edges and nodes opaque
    //on fast frames (opacity-0 layers stay hidden; settle restores user
    //values).
    var occlusionOn =
        (this.renderState.numElements.edgesdf || 0) > this.forceDepthOcclusionThreshold;
    var fastOpacity = fastPending && occlusionOn;
    this.updateOpacity(uniforms.edgesdf, 'edgeOpacity', 'edgesdf', fastOpacity);
    this.updateOpacity(uniforms.edgesdfarrow, 'edgeOpacity', 'edgesdfarrow', fastOpacity);
    //keep-first rejection is never exact (fragment alpha = opacity *
    //widthFade * AA coverage; rejection loses their accumulation), so only
    //occluded fast frames use stroke depth, with the fade off so survivors
    //are opaque
    uniforms.edgesdf.depthOcclusion[0] = fastOpacity ? 1 : 0;
    //normalize the id-ordered depth spread by the REAL instance count: a
    //constant ceiling silently clips edges whose id maps past ndc z=1
    uniforms.edgesdf.depthIdMax[0] = Math.max(1, this.renderState.numElements.edgesdf || 1);
    uniforms.edgesdf.widthFade[0] = fastOpacity ? 0 : 1;
    //the prepass IS exact (core-only depth, AA rim discarded), so it runs
    //whenever discs are effectively opaque; translucent discs would erase
    //the edges under them. pointOpacity is shared by fill/outline/depth.
    var effectiveNodeOpacity =
        this.updateOpacity(uniforms.pointculled, 'pointOpacity', 'points', fastOpacity);
    var prepassOn = effectiveNodeOpacity >= 0.999;
    this.renderer.setNumElements(this.renderState, 'pointdepth',
        prepassOn ? (this.renderState.numElements.pointculled || 0) : 0);
};

/*
 * Force an opacity uniform to 1 while `shouldForce`, restoring the user value
 * after. Never re-captures its own forced 1 as the user value; any other
 * value seen mid-force is a foreign (slider) write and is adopted. Hidden
 * layers (opacity 0) stay hidden -- they must not flash in or occlude.
 * Returns the value now in the uniform.
 */
RenderingScheduler.prototype.updateOpacity = function(owner, key, stateKey, shouldForce) {
    var store = this.forcedOpacity || (this.forcedOpacity = {});
    var rec = store[stateKey] || (store[stateKey] = { forced: false, user: owner[key][0] });
    if (!rec.forced || owner[key][0] !== 1) {
        rec.user = owner[key][0];
    }
    if (shouldForce && rec.user > 0) {
        rec.forced = true;
        owner[key][0] = 1;
    } else if (rec.forced) {
        rec.forced = false;
        owner[key][0] = rec.user;
    }
    return owner[key][0];
};

//typed-array view matching a texture def's channel type
var DATA_TEXTURE_ARRAYS = {
    //add rows as textures.js declares new channel types
    FLOAT: Float32Array
};

/*
 * Generic host-buffer -> data-texture mirror. Every config.dataTextures entry
 * declaring a `source` (see textures.js, e.g. nodePositionTexture <-
 * curPoints) is re-uploaded when the source buffer's received version moves:
 * laid out one texel per element (componentsPerTexel channels), width clamped
 * to MAX_TEXTURE_SIZE, in-place subUpdate after the first allocation. Shaders
 * recover the row width via textureSize.
 */
RenderingScheduler.prototype.syncDataTextures = function() {
    var renderState = this.renderState;
    var names = renderState.config.dataTextures;
    if (!names || names.length === 0) {
        return;
    }

    var appSnapshot = this.appSnapshot;
    var bufferSnapshots = appSnapshot.buffers;
    var versions = this.dataTextureVersions || (this.dataTextureVersions = {});
    var maxTextureSize = this.maxTextureSize ||
        (this.maxTextureSize = renderState.gl.getParameter(renderState.gl.MAX_TEXTURE_SIZE));

    for (var i = 0; i < names.length; i++) {
        var name = names[i];
        var def = renderState.config.textures[name];
        if (!bufferSnapshots[def.source]) {
            //not streamed yet; retry next frame
            continue;
        }
        var version = appSnapshot.bufferReceivedVersions[def.source];
        if (versions[name] === version) {
            continue;
        }

        var ArrayType = DATA_TEXTURE_ARRAYS[def.type];
        var source = castToTypedArray(bufferSnapshots[def.source], ArrayType);
        var elements = source.length / def.componentsPerTexel;
        var width = Math.max(1, Math.min(elements, maxTextureSize));
        var height = Math.max(1, Math.ceil(elements / width));

        var textureNfo = {};
        textureNfo[name] = {
            width: width,
            height: height,
            dataTexture: true,
            internalFormat: def.internalFormat,
            format: def.format,
            type: def.type,
            componentsPerTexel: def.componentsPerTexel,
            texelCount: elements,
            buffer: source
        };
        this.renderer.loadTextures(renderState, textureNfo);

        versions[name] = version;
    }
};

/*
 * Has a new edgeColors buffer arrived since the instance attrs were last
 * built? forceUpdate forces true (e.g. missing instance buffers). The
 * midsprings path keeps its own variant: it consumes conditionally and also
 * keys on length.
 */
RenderingScheduler.prototype.shouldUpdateEdgeColors = function(forceUpdate) {
    var appSnapshot = this.appSnapshot;
    var changed =
        appSnapshot.bufferReceivedVersions.edgeColors !==
            appSnapshot.bufferComputedVersions.edgeColors || Boolean(forceUpdate);
    //consume on check: the caller commits to rebuilding whenever this returns
    //true, so marking the version here cannot drop an update
    appSnapshot.bufferComputedVersions.edgeColors =
        appSnapshot.bufferReceivedVersions.edgeColors;
    return changed;
};

/*
 * Ensure the topology-cadence SDF instance buffers exist before a fast layout
 * draw: repeated calls are version checks only; the O(edges) bundle/color
 * work runs on first load or a real topology/encoding change.
 */
RenderingScheduler.prototype.updateEdgeInstancesForFastRender = function() {
    var appSnapshot = this.appSnapshot;
    var bufferSnapshots = appSnapshot.buffers;

    if (!this.renderState.config.items.edgesdf ||
        !bufferSnapshots.logicalEdges || !bufferSnapshots.curPoints ||
        !bufferSnapshots.forwardsEdgeToUnsortedEdge || !bufferSnapshots.edgeColors ||
        //Initial graph streaming can deliver topology in many partial chunks.
        //Let the ordinary first settle create the cold O(edges) buffers once;
        //fast layout frames only maintain an already-initialized SDF scene.
        !bufferSnapshots.edgeStyleAttrs) {
        return;
    }

    this.updateEdgeInstances(bufferSnapshots, this.lastEdgesData.curvature);
};

/*
 * Render the deferred hitmap if a settle marked it dirty: called from
 * scene.js pointer handlers, so the first hover after a settle pays the
 * picking cost instead of every settle. The 'picking' trigger tag skips
 * the fullScreenBufferDirty/isAnimating machinery.
 */
RenderingScheduler.prototype.renderPickingLazily = function() {
    if (!this.pickingDirty) {
        return;
    }
    this.pickingDirty = false;
    this.renderScene('lazypicking', {
        trigger: 'picking',
        callback: () => { this.appSnapshot.hitmapUpdates.onNext(); }
    });
};

/*
 * Render expensive items (eg, edges) once a quiet state is detected. Must
 * finish inside the animation frame it runs in -- no callbacks (rx, etc),
 * they would schedule work outside the frame.
 */
RenderingScheduler.prototype.renderSlowEffects = function() {
    var appSnapshot = this.appSnapshot;
    var renderState = this.renderState;
    var edgeMode = renderState.config.edgeMode;
    var clientMidEdgeInterpolation = renderState.config.clientMidEdgeInterpolation;
    var numRenderedSplits = renderState.config.numRenderedSplits;
    var start;
    var end1, end2, end3, end4;

    var expanded;
    var renderer = this.renderer;

    let midEdgesColors, midSpringsPos;
    let bufferSnapshots = appSnapshot.buffers;
    let curPoints = bufferSnapshots && bufferSnapshots.curPoints;
    let logicalEdges = bufferSnapshots && bufferSnapshots.logicalEdges;

    if (!curPoints) {
        // debug('Not doing renderSlowEffects because no nodes');
        return;
    }
    if (!logicalEdges) {
        // debug('Not doing renderSlowEffects because no edges');
        return;
    }

    //used for reverse edge highlights
    //TODO only compute if needed
    if (appSnapshot.vboUpdated) {
        const rit0ms = Date.now(); 
        const { backwardsEdgeStartEndIdxs, backwardsEdgeToUnsortedEdge } = this.reverseIndexIncomingEdges(bufferSnapshots);
        bufferSnapshots.backwardsEdgeStartEndIdxs = backwardsEdgeStartEndIdxs;
        bufferSnapshots.backwardsEdgeToUnsortedEdge = backwardsEdgeToUnsortedEdge;
        backwardsEdgeToUnsortedEdgeSubject.next(backwardsEdgeToUnsortedEdge);
        backwardsEdgeStartEndIdxsSubject.next(backwardsEdgeStartEndIdxs);
        console.debug('reverse index time (ms)', Date.now() - rit0ms);
    }

    //the (default) SDF renderer takes a separate settle path (per-instance
    //buffers, GPU arrows, lazy picking); the legacy CPU-expansion body below
    //serves ?legacyRenderer and is deleted wholesale when the fallback retires
    if (renderState.config.items.edgesdf) {
        this.renderSlowEffectsEdgesdf();
        return;
    }

    let { curvatureChanged, curvature } = this.lastEdgesData;

    if (clientMidEdgeInterpolation && (curvatureChanged || appSnapshot.vboUpdated)) {

        this.appSnapshot.vboUpdated = false;
        this.lastEdgesData.curvatureChanged = false;

        //ARCS
        start = Date.now();
        expanded = this.expandLogicalEdges(bufferSnapshots, numRenderedSplits, curvature);
        midSpringsPos = expanded.midSpringsPos;
        bufferSnapshots.midSpringsPos = midSpringsPos;
        bufferSnapshots.midSpringsStarts = expanded.midSpringsStarts;
        bufferSnapshots.midSpringsEnds = expanded.midSpringsEnds;

        // Only setup midEdge colors once, or when filtered.
        // Approximates filtering when number of logicalEdges changes.
        var numEdges = midSpringsPos.length / 2 / (numRenderedSplits + 1);
        var expectedNumMidEdgeColors = numEdges * (numRenderedSplits + 1);

        var shouldRecomputeEdgeColors = !bufferSnapshots.midEdgesColors ||
            bufferSnapshots.midEdgesColors.length !== expectedNumMidEdgeColors ||
            appSnapshot.bufferReceivedVersions.edgeColors !==
                appSnapshot.bufferComputedVersions.edgeColors;

        if (shouldRecomputeEdgeColors) {
            midEdgesColors = this.getMidEdgeColors(
                bufferSnapshots,
                numEdges,
                numRenderedSplits
            );
        }
        end1 = Date.now();
        if (shouldRecomputeEdgeColors) {
            bufferSnapshots.midEdgesColors = midEdgesColors;
            renderer.loadBuffers(renderState, { midEdgesColors: midEdgesColors });
            appSnapshot.bufferComputedVersions.edgeColors =
                appSnapshot.bufferReceivedVersions.edgeColors;
        }

        renderer.loadBuffers(renderState, { midSpringsPos: midSpringsPos });
        renderer.loadBuffers(renderState, { midSpringsStarts: expanded.midSpringsStarts });
        renderer.loadBuffers(renderState, { midSpringsEnds: expanded.midSpringsEnds });
        renderer.setNumElements(renderState, 'edgepicking', midSpringsPos.length / 2);
        renderer.setNumElements(renderState, 'midedgeculled', midSpringsPos.length / 2);
        end2 = Date.now();
        // debug('Edges expanded in', end1 - start, '[ms], and loaded in', end2 - end1, '[ms]');
        this.makeArrows(bufferSnapshots, edgeMode, numRenderedSplits);
        end3 = Date.now();
        renderer.loadBuffers(renderState, { arrowStartPos: bufferSnapshots.arrowStartPos });
        renderer.loadBuffers(renderState, { arrowEndPos: bufferSnapshots.arrowEndPos });
        renderer.loadBuffers(renderState, { arrowNormalDir: bufferSnapshots.arrowNormalDir });
        renderer.loadBuffers(renderState, { arrowColors: bufferSnapshots.arrowColors });
        renderer.loadBuffers(renderState, { arrowPointSizes: bufferSnapshots.arrowPointSizes });

        // numEdges = length / 4 (stored as UInt8) * 0.5 (biDirectional)
        // numArrowElements = 3 * numEdges.
        var numArrowCulled = bufferSnapshots.logicalEdges.length / 2 / 4 * 3;

        renderer.setNumElements(renderState, 'arrowculled', numArrowCulled);
        end4 = Date.now();

        // debug('Arrows generated in ', end3 - end2, '[ms], and loaded in', end4 - end3, '[ms]');
    }

    //picking (FBO + readback) first, fullscene second: the readback ends
    //rebound to the default framebuffer, and the frame that rebind opens
    //must present a real canvas draw before the task ends (an unpresented
    //frame stalls Firefox's next composite ~650ms)
    renderer.render(renderState, 'picking', 'picking', undefined, undefined,
        () => { this.appSnapshot.hitmapUpdates.onNext(); }
    );
    renderer.render(renderState, 'fullscene', 'renderSceneFull');

    // TODO: Make steadyStateTextureDark instead of just doing it in the shader.
    renderer.copyCanvasToTexture(renderState, 'steadyStateTexture');
    renderer.setupFullscreenBuffer(renderState);
    this.renderMouseoverEffects(this.lastMouseoverTask);

    this.appSnapshot.fullScreenBufferDirty = false;
};

//Default-renderer settle: per-instance buffers instead of CPU segment
//expansion, GPU arrowheads, and lazy picking. Mirrors renderSlowEffects'
//shared tail (picking -> fullscene -> steady-state copy -> mouseover); when
//the ?legacyRenderer fallback retires this replaces renderSlowEffects outright.
RenderingScheduler.prototype.renderSlowEffectsEdgesdf = function() {
    var appSnapshot = this.appSnapshot;
    var renderState = this.renderState;
    var clientMidEdgeInterpolation = renderState.config.clientMidEdgeInterpolation;
    var renderer = this.renderer;
    var bufferSnapshots = appSnapshot.buffers;

    let { curvatureChanged, curvature } = this.lastEdgesData;

    if (clientMidEdgeInterpolation && (curvatureChanged || appSnapshot.vboUpdated)) {
        this.appSnapshot.vboUpdated = false;
        this.lastEdgesData.curvatureChanged = false;

        var tInst0 = Date.now();
        this.updateEdgeInstances(bufferSnapshots, curvature);
        console.debug('edge pass timings (ms)', { edgesdfInstances: Date.now() - tInst0 });
    }

    //pointsampling now (labels); the hitmap (a fullscene-cost pass at ~10M) waits
    //for the first hover, and the deleted read-buffer reports no-hit, not a mis-pick,
    //until it lands. Readback first: its default-framebuffer rebind opens a frame
    //Firefox's compositor waits on -- fullscene presents it, then the copy reads it
    delete renderState.pixelreads.hitmap;
    this.pickingDirty = true;
    renderer.render(renderState, 'labelsampling', 'picking', ['pointsampling'], undefined,
        () => { this.appSnapshot.hitmapUpdates.onNext(); }
    );
    renderer.render(renderState, 'fullscene', 'renderSceneFull');

    // TODO: Make steadyStateTextureDark instead of just doing it in the shader.
    renderer.copyCanvasToTexture(renderState, 'steadyStateTexture');
    renderer.setupFullscreenBuffer(renderState);
    this.renderMouseoverEffects(this.lastMouseoverTask);

    this.appSnapshot.fullScreenBufferDirty = false;
};

RenderingScheduler.prototype.renderMovePointsOverlay = function(task) {
    var { appSnapshot, renderState, renderer } = this;
    var { buffers } = appSnapshot;
    var { diff, sel } = task.data;
    var hostBuffers = renderState.hostBuffersCache;

    var hostNodeSizes = hostBuffers.pointSizes;
    var hostNodeColors = castToTypedArray(hostBuffers.pointColors, Uint32Array);

    var movingNodeIndices = sel.getPointIndexValues();
    var numMovingNodes = movingNodeIndices.length;

    renderer.setNumElements(renderState, 'edgeselected', 0);
    renderer.setNumElements(renderState, 'edgesdfselected', 0);
    renderer.setNumElements(renderState, 'pointselected', numMovingNodes);
    renderer.setNumElements(renderState, 'arrowselected', 0);
    renderer.setNumElements(renderState, 'edgesdfarrowselected', 0);

    // Create empty arrays for edges and arrows
    buffers.selectedEdges = new Float32Array(0);
    buffers.selectedEdgeStarts = new Float32Array(0);
    buffers.selectedEdgeEnds = new Float32Array(0);
    buffers.selectedEdgeColors = new Uint32Array(0);
    buffers.selectedArrowStartPos = new Float32Array(0);
    buffers.selectedArrowEndPos = new Float32Array(0);
    buffers.selectedArrowNormalDir = new Float32Array(0);
    buffers.selectedArrowPointColors = new Uint32Array(0);
    buffers.selectedArrowPointSizes = new Uint8Array(0);

    // Create arrays for nodes
    buffers.selectedNodeIds = new Uint32Array(movingNodeIndices.length);
    buffers.selectedNodeSizes = new Uint8Array(movingNodeIndices.length);
    buffers.selectedNodeColors = new Uint32Array(movingNodeIndices.length);

    let { selectedNodeIds, selectedNodeSizes, selectedNodeColors } = buffers;

    // Copy in node information
    _.each(movingNodeIndices, function(val, idx) {
        selectedNodeIds[idx] = val;
        selectedNodeSizes[idx] = hostNodeSizes[val];
        selectedNodeColors[idx] = hostNodeColors[val];
    });

    //drag preview: the overlay fetches LIVE positions by node id, so the
    //not-yet-committed drag delta rides as a world-space uniform shift
    renderState.uniforms.pointselected.posOffset = [diff.x, diff.y];
    renderState.uniforms.pointselectedoutline.posOffset = [diff.x, diff.y];

    renderer.loadBuffers(renderState, {
        selectedMidSpringsPos: buffers.selectedEdges,
        selectedMidEdgesColors: buffers.selectedEdgeColors,
        selectedMidSpringsStarts: buffers.selectedEdgeStarts,
        selectedMidSpringsEnds: buffers.selectedEdgeEnds,
        pointSelectedNodeIds: buffers.selectedNodeIds,
        selectedPointSizes: buffers.selectedNodeSizes,
        selectedPointColors: buffers.selectedNodeColors,
        selectedArrowStartPos: buffers.selectedArrowStartPos,
        selectedArrowEndPos: buffers.selectedArrowEndPos,
        selectedArrowNormalDir: buffers.selectedArrowNormalDir,
        selectedArrowColors: buffers.selectedArrowPointColors,
        selectedArrowPointSizes: buffers.selectedArrowPointSizes
    });

    renderer.render(renderState, 'highlightDark', 'highlightDark');
};

/*
 * Render mouseover effects. These should only occur during a quiet state.
 *
 */

RenderingScheduler.prototype.renderMouseoverEffects = function(task) {
    var renderer = this.renderer;
    var appSnapshot = this.appSnapshot;
    var renderState = this.renderState;
    var buffers = appSnapshot.buffers;
    var numRenderedSplits = renderState.config.numRenderedSplits;
    var numMidEdges = numRenderedSplits + 1;
    const numPoints = buffers.pointSizes.length;
    
    // We haven't received any VBOs yet, so we shouldn't attempt to render.
    // In edgesdf mode there is no full midSpringsPos; highlight geometry is
    // expanded per subset from the height-class data instead.
    var edgesdfMode = !buffers.midSpringsPos && !!this.edgeInstanceShapeFactors;
    if (!buffers.logicalEdges || (!buffers.midSpringsPos && !edgesdfMode)) {
        return;
    } else if (!task && !this.lastMouseoverTask) {
        return;
    }

    //////////////////////////////////////////////////////////////////////////
    // Expand highlighted neighborhoods
    //////////////////////////////////////////////////////////////////////////

    var logicalEdges = castToTypedArray(buffers.logicalEdges, Uint32Array);
    var hostBuffers = renderState.hostBuffersCache;
    var forwardsEdgeStartEndIdxs = castToTypedArray(hostBuffers.forwardsEdgeStartEndIdxs, Int32Array);
    var backwardsEdgeStartEndIdxs = castToTypedArray(buffers.backwardsEdgeStartEndIdxs, Int32Array);

    var forwardsEdgeToUnsortedEdge = castToTypedArray(hostBuffers.forwardsEdgeToUnsortedEdge, Uint32Array);
    var backwardsEdgeToUnsortedEdge = castToTypedArray(buffers.backwardsEdgeToUnsortedEdge, Uint32Array);

    var hostNodePositions = castToTypedArray(hostBuffers.curPoints, Float32Array);
    var hostNodeSizes = hostBuffers.pointSizes;
    var hostNodeColors = castToTypedArray(hostBuffers.pointColors, Uint32Array);

    var highlightedNodeIndices;
    var highlightedEdgeIndices;
    var selectedNodeIndices;
    var selectedEdgeIndices;
    var initialHighlightLengths;
    var initialSelectedLengths;

    if (!task) {
        task = this.lastMouseoverTask;
        highlightedNodeIndices = task.data.highlight.nodeIndices || [];
        highlightedEdgeIndices = task.data.highlight.edgeIndices || [];
        selectedNodeIndices = task.data.selected.nodeIndices || [];
        selectedEdgeIndices = task.data.selected.edgeIndices || [];
        initialHighlightLengths = 0;
        initialSelectedLengths = 0;
    } else {
        selectedNodeIndices = task.data.selected.nodeIndices || [];
        selectedEdgeIndices = task.data.selected.edgeIndices || [];
        highlightedNodeIndices = task.data.highlight.nodeIndices || [];
        highlightedEdgeIndices = task.data.highlight.edgeIndices || [];
        initialSelectedLengths = selectedEdgeIndices.length + selectedNodeIndices.length;
        initialHighlightLengths = highlightedEdgeIndices.length + highlightedNodeIndices.length;

        const isSameSelectedArrays = this.lastMouseoverTask &&
            selectedNodeIndices === this.lastMouseoverTask.data.selected.nodeIndices &&
            selectedEdgeIndices === this.lastMouseoverTask.data.selected.edgeIndices;

        if (!this.lastMouseoverTask || (
            task !== this.lastMouseoverTask && (!isSameSelectedArrays ||
            highlightedNodeIndices !== this.lastMouseoverTask.data.highlight.nodeIndices ||
            highlightedEdgeIndices !== this.lastMouseoverTask.data.highlight.edgeIndices))) {

            if (initialHighlightLengths <= 1) {
                wavefrontExpand({
                    highlightedNodeIndices,
                    highlightedEdgeIndices,
                    neighborhoodHighlightHops: task.data.neighborhoodHighlightHops,
                    neighborhoodHighlight: task.data.neighborhoodHighlight,
                    forwardsEdgeStartEndIdxs,
                    backwardsEdgeStartEndIdxs,
                    forwardsEdgeToUnsortedEdge,
                    backwardsEdgeToUnsortedEdge,
                    logicalEdges,
                    numPoints,
                });
                initialHighlightLengths = highlightedEdgeIndices.length + highlightedNodeIndices.length;
            }

            if (task.data.darken && !isSameSelectedArrays && selectedNodeIndices.length > 0) {
                let selectedNodeIndicesIndex = selectedNodeIndices.length;
                // Extend node indices with edge endpoints
                for (let index = -1, length = selectedEdgeIndices.length; ++index < length; ) {
                    const stride = 2 * selectedEdgeIndices[index];
                    selectedNodeIndices[selectedNodeIndicesIndex++] = logicalEdges[stride];
                    selectedNodeIndices[selectedNodeIndicesIndex++] = logicalEdges[stride + 1];
                }
                initialSelectedLengths = selectedEdgeIndices.length + selectedNodeIndices.length;
            }

            // Cache a copy of the task in case we need to execute again with our last task.
            // We need to be careful not to accidentally modify the internals of this cached task.
            this.lastMouseoverTask = task;
        }
    }

    //////////////////////////////////////////////////////////////////////////
    // Setup highlight buffers
    //////////////////////////////////////////////////////////////////////////

    renderer.setNumElements(
        renderState,
        'edgehighlight',
        highlightedEdgeIndices.length * 2 * numMidEdges
    );
    renderer.setNumElements(renderState, 'edgesdfhighlight', highlightedEdgeIndices.length);
    renderer.setNumElements(renderState, 'edgesdfarrowhighlight', highlightedEdgeIndices.length);
    renderer.setNumElements(renderState, 'pointhighlight', highlightedNodeIndices.length);
    renderer.setNumElements(renderState, 'pointhighlightoutline', highlightedNodeIndices.length);
    renderer.setNumElements(renderState, 'arrowhighlight', highlightedEdgeIndices.length * 3);

    if (initialHighlightLengths > 0) {
        // TODO: Start with a small buffer and increase if necessary, masking underlying
        // data so we don't have to clear out later values. This way we won't have to constantly allocate
        buffers.highlightedEdges = new Float32Array(
            highlightedEdgeIndices.length * 4 * numMidEdges
        );
        buffers.highlightedNodeIds = new Uint32Array(highlightedNodeIndices.length);
        buffers.highlightedNodeSizes = new Uint8Array(highlightedNodeIndices.length);
        buffers.highlightedNodeColors = new Uint32Array(highlightedNodeIndices.length);
        buffers.highlightedArrowStartPos = new Float32Array(highlightedEdgeIndices.length * 2 * 3);
        buffers.highlightedArrowEndPos = new Float32Array(highlightedEdgeIndices.length * 2 * 3);
        buffers.highlightedArrowNormalDir = new Float32Array(highlightedEdgeIndices.length * 3);
        buffers.highlightedArrowPointColors = new Uint32Array(highlightedEdgeIndices.length * 3);
        buffers.highlightedArrowPointSizes = new Uint8Array(highlightedEdgeIndices.length * 3);

        let {
            highlightedEdges,
            midSpringsPos,
            highlightedNodeIds,
            highlightedNodeSizes,
            highlightedNodeColors
        } = buffers;

        if (edgesdfMode) {
            var hlSubset = buildEdgeOverlayInstances(
                highlightedEdgeIndices,
                logicalEdges,
                castToTypedArray(buffers.edgeColors, Uint32Array),
                this.edgeInstanceShapeFactors,
                EDGE_HIGHLIGHT_COLOR,
                castToTypedArray(hostNodeSizes, Uint8Array)
            );
            renderer.loadBuffers(renderState, {
                edgeHighlightEndpointIds: hlSubset.nodeIds,
                edgeHighlightStyleAttrs: hlSubset.attrs
            });
        } else {
            // Copy in data
            _.each(highlightedEdgeIndices, function(val, idx) {
                // The start at the first midedge corresponding to hovered edge
                var edgeStartIdx = val * 4 * numMidEdges;
                var highlightStartIdx = idx * 4 * numMidEdges;
                for (var midEdgeIdx = 0; midEdgeIdx < numMidEdges; midEdgeIdx = midEdgeIdx + 1) {
                    var midEdgeStride = midEdgeIdx * 4;
                    highlightedEdges[highlightStartIdx + midEdgeStride] =
                        midSpringsPos[edgeStartIdx + midEdgeStride];
                    highlightedEdges[highlightStartIdx + midEdgeStride + 1] =
                        midSpringsPos[edgeStartIdx + midEdgeStride + 1];
                    highlightedEdges[highlightStartIdx + midEdgeStride + 2] =
                        midSpringsPos[edgeStartIdx + midEdgeStride + 2];
                    highlightedEdges[highlightStartIdx + midEdgeStride + 3] =
                        midSpringsPos[edgeStartIdx + midEdgeStride + 3];
                }
            });
        }

        _.each(highlightedNodeIndices, function(val, idx) {
            highlightedNodeIds[idx] = val;
            highlightedNodeSizes[idx] = hostNodeSizes[val];
            highlightedNodeColors[idx] = hostNodeColors[val];
        });

        //edgesdfarrowhighlight renders overlay arrowheads on the GPU from the
        //subset instance buffers; the CPU build only feeds legacy arrowhighlight
        if (!edgesdfMode) {
            this.populateArrowBuffers(
                highlightedEdgeIndices,
                buffers.midSpringsPos,
                buffers.highlightedArrowStartPos,
                buffers.highlightedArrowEndPos,
                buffers.highlightedArrowNormalDir,
                hostNodeSizes,
                logicalEdges,
                buffers.highlightedArrowPointSizes,
                buffers.highlightedArrowPointColors,
                buffers.edgeColors,
                numRenderedSplits
            );
        }

        renderer.loadBuffers(renderState, {
            highlightedEdgesPos: buffers.highlightedEdges,
            pointHighlightNodeIds: buffers.highlightedNodeIds,
            highlightedPointsSizes: buffers.highlightedNodeSizes,
            highlightedPointsColors: buffers.highlightedNodeColors,
            highlightedArrowStartPos: buffers.highlightedArrowStartPos,
            highlightedArrowEndPos: buffers.highlightedArrowEndPos,
            highlightedArrowNormalDir: buffers.highlightedArrowNormalDir,
            highlightedArrowPointColors: buffers.highlightedArrowPointColors,
            highlightedArrowPointSizes: buffers.highlightedArrowPointSizes
        });
    }

    //////////////////////////////////////////////////////////////////////////
    // Setup selected buffers
    //////////////////////////////////////////////////////////////////////////

    // TODO: Start with a small buffer and increase if necessary, masking underlying
    // data so we don't have to clear out later values. This way we won't have to constantly allocate

    renderer.setNumElements(
        renderState,
        'edgeselected',
        selectedEdgeIndices.length * 2 * numMidEdges
    );
    renderer.setNumElements(renderState, 'edgesdfselected', selectedEdgeIndices.length);
    renderer.setNumElements(renderState, 'edgesdfarrowselected', selectedEdgeIndices.length);
    renderer.setNumElements(renderState, 'pointselected', selectedNodeIndices.length);
    renderer.setNumElements(renderState, 'pointselectedoutline', selectedNodeIndices.length);
    //undo any move-drag preview shift (renderMovePointsOverlay)
    renderState.uniforms.pointselected.posOffset = [0.0, 0.0];
    renderState.uniforms.pointselectedoutline.posOffset = [0.0, 0.0];
    renderer.setNumElements(renderState, 'arrowselected', selectedEdgeIndices.length * 3);

    if (initialSelectedLengths > 0) {
        buffers.selectedEdges = new Float32Array(selectedEdgeIndices.length * 4 * numMidEdges);
        buffers.selectedEdgeStarts = new Float32Array(selectedEdgeIndices.length * 4 * numMidEdges);
        buffers.selectedEdgeEnds = new Float32Array(selectedEdgeIndices.length * 4 * numMidEdges);
        buffers.selectedEdgeColors = new Uint32Array(selectedEdgeIndices.length * 2 * numMidEdges);
        buffers.selectedNodeIds = new Uint32Array(selectedNodeIndices.length);
        buffers.selectedNodeSizes = new Uint8Array(selectedNodeIndices.length);
        buffers.selectedNodeColors = new Uint32Array(selectedNodeIndices.length);
        buffers.selectedArrowStartPos = new Float32Array(selectedEdgeIndices.length * 2 * 3);
        buffers.selectedArrowEndPos = new Float32Array(selectedEdgeIndices.length * 2 * 3);
        buffers.selectedArrowNormalDir = new Float32Array(selectedEdgeIndices.length * 3);
        buffers.selectedArrowPointColors = new Uint32Array(selectedEdgeIndices.length * 3);
        buffers.selectedArrowPointSizes = new Uint8Array(selectedEdgeIndices.length * 3);

        let {
            selectedEdges,
            selectedEdgeStarts,
            selectedEdgeEnds,
            selectedEdgeColors,
            midEdgesColors,
            midSpringsPos,
            midSpringsStarts,
            midSpringsEnds,
            selectedNodeIds,
            selectedNodeSizes,
            selectedNodeColors
        } = buffers;

        if (edgesdfMode) {
            var selSubset = buildEdgeOverlayInstances(
                selectedEdgeIndices,
                logicalEdges,
                castToTypedArray(buffers.edgeColors, Uint32Array),
                this.edgeInstanceShapeFactors,
                undefined,
                castToTypedArray(hostNodeSizes, Uint8Array)
            );
            renderer.loadBuffers(renderState, {
                edgeSelectedEndpointIds: selSubset.nodeIds,
                edgeSelectedStyleAttrs: selSubset.attrs
            });
        } else {
            // Copy in data
            _.each(selectedEdgeIndices, function(val, idx) {
                // The start at the first midedge corresponding to hovered edge
                var edgeStartIdx = val * 4 * numMidEdges;
                var highlightStartIdx = idx * 4 * numMidEdges;
                var edgeColorStartIdx = val * 2 * numMidEdges;
                var highlightColorStartIdx = idx * 2 * numMidEdges;
                for (var midEdgeIdx = 0; midEdgeIdx < numMidEdges; midEdgeIdx++) {
                    var midEdgeStride = midEdgeIdx * 4;
                    selectedEdges[highlightStartIdx + midEdgeStride] =
                        midSpringsPos[edgeStartIdx + midEdgeStride];
                    selectedEdges[highlightStartIdx + midEdgeStride + 1] =
                        midSpringsPos[edgeStartIdx + midEdgeStride + 1];
                    selectedEdges[highlightStartIdx + midEdgeStride + 2] =
                        midSpringsPos[edgeStartIdx + midEdgeStride + 2];
                    selectedEdges[highlightStartIdx + midEdgeStride + 3] =
                        midSpringsPos[edgeStartIdx + midEdgeStride + 3];

                    selectedEdgeStarts[highlightStartIdx + midEdgeStride] =
                        midSpringsStarts[edgeStartIdx + midEdgeStride];
                    selectedEdgeStarts[highlightStartIdx + midEdgeStride + 1] =
                        midSpringsStarts[edgeStartIdx + midEdgeStride + 1];
                    selectedEdgeStarts[highlightStartIdx + midEdgeStride + 2] =
                        midSpringsStarts[edgeStartIdx + midEdgeStride + 2];
                    selectedEdgeStarts[highlightStartIdx + midEdgeStride + 3] =
                        midSpringsStarts[edgeStartIdx + midEdgeStride + 3];

                    selectedEdgeEnds[highlightStartIdx + midEdgeStride] =
                        midSpringsEnds[edgeStartIdx + midEdgeStride];
                    selectedEdgeEnds[highlightStartIdx + midEdgeStride + 1] =
                        midSpringsEnds[edgeStartIdx + midEdgeStride + 1];
                    selectedEdgeEnds[highlightStartIdx + midEdgeStride + 2] =
                        midSpringsEnds[edgeStartIdx + midEdgeStride + 2];
                    selectedEdgeEnds[highlightStartIdx + midEdgeStride + 3] =
                        midSpringsEnds[edgeStartIdx + midEdgeStride + 3];

                    var midEdgeColorStride = midEdgeIdx * 2;
                    selectedEdgeColors[highlightColorStartIdx + midEdgeColorStride] =
                        midEdgesColors[edgeColorStartIdx + midEdgeColorStride];
                    selectedEdgeColors[highlightColorStartIdx + midEdgeColorStride + 1] =
                        midEdgesColors[edgeColorStartIdx + midEdgeColorStride + 1];
                }
            });
        }

        _.each(selectedNodeIndices, function(val, idx) {
            selectedNodeIds[idx] = val;
            selectedNodeSizes[idx] = hostNodeSizes[val];
            selectedNodeColors[idx] = hostNodeColors[val];
        });

        //edgesdfarrowselected renders overlay arrowheads on the GPU from the
        //subset instance buffers; the CPU build only feeds legacy arrowselected
        if (!edgesdfMode) {
            this.populateArrowBuffers(
                selectedEdgeIndices,
                buffers.midSpringsPos,
                buffers.selectedArrowStartPos,
                buffers.selectedArrowEndPos,
                buffers.selectedArrowNormalDir,
                hostNodeSizes,
                logicalEdges,
                buffers.selectedArrowPointSizes,
                buffers.selectedArrowPointColors,
                buffers.edgeColors,
                numRenderedSplits
            );
        }

        renderer.loadBuffers(renderState, {
            selectedMidSpringsPos: buffers.selectedEdges,
            selectedMidEdgesColors: buffers.selectedEdgeColors,
            selectedMidSpringsStarts: buffers.selectedEdgeStarts,
            selectedMidSpringsEnds: buffers.selectedEdgeEnds,
            pointSelectedNodeIds: buffers.selectedNodeIds,
            selectedPointSizes: buffers.selectedNodeSizes,
            selectedPointColors: buffers.selectedNodeColors,
            selectedArrowStartPos: buffers.selectedArrowStartPos,
            selectedArrowEndPos: buffers.selectedArrowEndPos,
            selectedArrowNormalDir: buffers.selectedArrowNormalDir,
            selectedArrowColors: buffers.selectedArrowPointColors,
            selectedArrowPointSizes: buffers.selectedArrowPointSizes
        });
    }

    //////////////////////////////////////////////////////////////////////////
    // Handle Rendering + Texture backdrop.
    //////////////////////////////////////////////////////////////////////////

    var shouldDarken =
        task.data.darken || selectedEdgeIndices.length > 0 || selectedNodeIndices.length > 0;
    var renderTrigger = shouldDarken ? 'highlightDark' : 'highlight';

    renderer.render(renderState, renderTrigger, renderTrigger);
};

RenderingScheduler.prototype.loadRadialAxes = function loadRadialAxes(axes, background) {

    const subradialColor = { r: 0, g: 0, b: 0, a: 0.1 };
    // const subradialColor = isDark(Color(background.color).rgb())
    //     ? { r: 255 / 256, g: 255 / 256, b: 255 / 256, a: 0.3 }
    //     : { r: 0, g: 0, b: 0, a: 0.1 };

    let { renderer, renderState } = this;
    let radialAxes = (axes || []).filter(({ r }) => typeof r === 'number');
    const axisStyles = {
        internal: { r: 89 / 256, g: 162 / 256, b: 255 / 256, a: 0.999 },
        external: { r: 255 / 256, g: 128 / 256, b: 64 / 256, a: 0.999 },
        minor: subradialColor,
        space: subradialColor
    };
    let x = 0,
        y = 0,
        maxStrokeWidth = 10,
        numRadialAxes = radialAxes.length;
    let radialAxesBuffer = this.getTypedArray('radialAxes', Float32Array, numRadialAxes * 3 * 6);
    for (let j = -1, i = -1; ++i < numRadialAxes; ) {
        let axis = radialAxes[i];
        let r = axis.r;
        let isThin = axis.space;
        let color =
            axisStyles[
                axis.internal
                    ? 'internal'
                    : axis.external ? 'external' : axis.space ? 'space' : 'minor'
            ];
        let flags =
            ((isThin ? 1 : 0) << 16) +
            (Math.floor(color.r * 16) << 12) +
            (Math.floor(color.g * 16) << 8) +
            (Math.floor(color.b * 16) << 4) +
            Math.floor(color.a * 16);

        radialAxesBuffer[++j] = x - r - maxStrokeWidth;
        radialAxesBuffer[++j] = y - r - maxStrokeWidth;
        radialAxesBuffer[++j] = x;
        radialAxesBuffer[++j] = y;
        radialAxesBuffer[++j] = r;
        radialAxesBuffer[++j] = flags;

        radialAxesBuffer[++j] = x + (1 + Math.sqrt(2)) * (r + maxStrokeWidth);
        radialAxesBuffer[++j] = y - r - maxStrokeWidth;
        radialAxesBuffer[++j] = x;
        radialAxesBuffer[++j] = y;
        radialAxesBuffer[++j] = r;
        radialAxesBuffer[++j] = flags;

        radialAxesBuffer[++j] = x - r - maxStrokeWidth;
        radialAxesBuffer[++j] = y + (1 + Math.sqrt(2)) * (r + maxStrokeWidth);
        radialAxesBuffer[++j] = x;
        radialAxesBuffer[++j] = y;
        radialAxesBuffer[++j] = r;
        radialAxesBuffer[++j] = flags;
    }
    renderer.loadBuffers(renderState, { radialAxes: radialAxesBuffer });
    renderer.setNumElements(renderState, 'radialaxes', numRadialAxes * 3);
};

RenderingScheduler.prototype.renderMovePointsTemporaryPositions = function(diff, sel) {
    this.renderScene('movePointsOverlay', { data: { diff, sel } });
};

// Given a render config and info about number of nodes/edges,
// generate an array of functions that will allocate memory for them
RenderingScheduler.prototype.allocateAllArrayBuffersFactory = function(
    config,
    numElements,
    renderState
) {
    var functions = [],
        renderer = this.renderer;
    // debug('Allocating all arraybuffers on hint for numElements: ', numElements);
    _.each(config.models, (model, modelName) => {
        _.each(model, desc => {
            if (typeof desc.sizeHint === 'function') {
                // Default to 4;
                // TODO: Have a proper lookup for bytelengths
                var bytesPerElement = 4;
                if (desc.type === 'FLOAT') {
                    bytesPerElement = 4;
                } else if (desc.type === 'UNSIGNED_INT') {
                    bytesPerElement = 4;
                } else if (desc.type === 'UNSIGNED_BYTE') {
                    bytesPerElement = 1;
                }

                // It compute a size hint from the render config.
                var sizeInBytes = desc.sizeHint(numElements) * desc.count * bytesPerElement;

                if (!isNaN(sizeInBytes)) {
                    // Allocate arraybuffers for RenderingScheduler
                    functions.push(() => {
                        this.allocateArrayBufferOnHint(modelName, sizeInBytes);
                    });
                    // Allocate GPU buffer in renderer
                    functions.push(() => {
                        renderer.allocateBufferSize(renderState, modelName, sizeInBytes);
                    });
                }
            }
        });
    });
    return functions;
};

// Explicitly allocate an array buffer for a given name based on a size hint
RenderingScheduler.prototype.allocateArrayBufferOnHint = function(name, bytes) {
    // debug(`Hinted allocation of %d bytes for %s`, bytes, name);
    if (!this.arrayBuffers[name] || this.arrayBuffers[name].byteLength < bytes) {
        // debug('Allocating', bytes, 'bytes for', name, 'on hint.');
        this.arrayBuffers[name] = new ArrayBuffer(bytes);
    }
};

// Get a typed array by name, of a certain type and length.
// We go through this function to allow arraybuffer reuse,
// and to make preallocation easier. Because we reuse data buffers,
// older typed arrays of the same name are invalidated.
RenderingScheduler.prototype.getTypedArray = function(name, Constructor, length) {
    var bytesPerElement = Constructor.BYTES_PER_ELEMENT;
    var lengthInBytes = length * bytesPerElement;
    // debug('getting typed array for ' + name + ':', Constructor, length, lengthInBytes);
    // TODO: Check to make sure that we don't leak references to old
    // array buffers when we replace with a bigger one.
    if (!this.arrayBuffers[name] || this.arrayBuffers[name].byteLength < lengthInBytes) {
        // debug('Reallocating for %s to %d bytes (%d elements)', name, lengthInBytes, lengthInBytes / Constructor.BYTES_PER_ELEMENT);
        // debug('Old byteLength: %d', this.arrayBuffers[name] ? this.arrayBuffers[name].byteLength : 0);
        this.arrayBuffers[name] = new ArrayBuffer(lengthInBytes);
    } else {
        // debug(`Buffer %s was in cache of proper size %d bytes (%d elements) -- fast path`, name, lengthInBytes, lengthInBytes / Constructor.BYTES_PER_ELEMENT);
    }

    var array = new Constructor(this.arrayBuffers[name], 0, length);
    return array;
};

// Given a render config and info about number of nodes/edges,
// figure out the size of our largest model for letting the
// renderer create index buffers.
RenderingScheduler.prototype.getLargestModelSize = function(config, numElements) {
    // debug('Getting largest model size for: ', numElements);
    var sizes = _.map(config.models, function(model) {
        return _.map(model, function(desc) {
            if (typeof desc.sizeHint === 'function') {
                // Compute a size hint from the render config.
                return desc.sizeHint(numElements);
            } else {
                return 0;
            }
        });
    });
    var maxNum = _.max(_.flatten(sizes));
    return maxNum;
};

RenderingScheduler.prototype.dispose = RenderingScheduler.prototype._unsubscribe = function() {
    const { hintsAllocationCycle } = this;

    if (hintsAllocationCycle) {
        hintsAllocationCycle.unsubscribe();
    }
    this.hintsAllocationCycle = null;
};

function castToTypedArray(array, Type) {
    return (array instanceof Type) ? array :
        new Type(array.buffer, array.byteOffset, array.byteLength / Type.BYTES_PER_ELEMENT);
}

export { RenderingScheduler };
export default RenderingScheduler;
